import { useRef, useState, type KeyboardEvent, type DragEvent } from "react";
import { useTranslation } from "react-i18next";

import type { NotificationChannelName } from "@/lib/notifications-api";

/**
 * apps/web :: components :: ChannelOrderList
 *
 * Reorderable list of enabled notification channels used by
 * `NotificationPreferences` to let the user rank fallback priority. The
 * first enabled channel is tried first; the next acts as a fallback, and
 * so on.
 *
 * Accessibility-first (E41.F* / WCAG 2.1 AA):
 *   - Each row is a native <li role="listitem"> with a visible position
 *     badge and Up / Down buttons. Both buttons are keyboard-reachable
 *     (tab order matches visual order) and expose an aria-label that
 *     includes the channel name so screen readers announce the action.
 *   - A non-button drag handle (role="button", tabindex=0) exposes
 *     ArrowUp / ArrowDown keyboard semantics AND is the pointer drag
 *     source via native HTML5 drag-and-drop (no extra runtime deps).
 *   - Focus is preserved on the moved row after a keyboard move so
 *     repeated Arrow presses keep nudging the same item.
 *
 * This component is intentionally presentational — the parent owns the
 * source of truth and passes `onChange(newOrder)` receives the new
 * ordered list.
 */
export interface OrderableChannel {
  id: NotificationChannelName;
  /** Localised channel label (provided by the caller). */
  label: string;
}

export interface ChannelOrderListProps {
  /** Ordered list of enabled channels. First = highest priority. */
  channels: OrderableChannel[];
  /** Called with the reordered list whenever the user changes the order. */
  onChange: (ordered: OrderableChannel[]) => void;
}

export default function ChannelOrderList({
  channels,
  onChange,
}: ChannelOrderListProps) {
  const { t } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Identifier of the row whose handle should be re-focused after a
  // keyboard-driven reorder. Cleared after the focus effect runs.
  const refocusIdRef = useRef<string | null>(null);

  if (channels.length === 0) {
    return (
      <p
        data-testid="channel-order-empty"
        className="text-xs text-neutral-600"
      >
        {t("notifications.channelOrder.empty")}
      </p>
    );
  }

  function move(fromIndex: number, toIndex: number, refocusId?: string) {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= channels.length ||
      toIndex >= channels.length
    ) {
      return;
    }
    const next = channels.slice();
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    if (refocusId) {
      refocusIdRef.current = refocusId;
    }
    onChange(next);
  }

  function onDragStart(ev: DragEvent<HTMLLIElement>, index: number) {
    setDragIndex(index);
    // Required for Firefox to start a drag session.
    ev.dataTransfer.effectAllowed = "move";
    ev.dataTransfer.setData("text/plain", String(index));
  }

  function onDragOver(ev: DragEvent<HTMLLIElement>) {
    // Default behaviour forbids drop — prevent it so drop fires.
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "move";
  }

  function onDrop(ev: DragEvent<HTMLLIElement>, targetIndex: number) {
    ev.preventDefault();
    const raw = ev.dataTransfer.getData("text/plain");
    const sourceIndex = raw === "" ? dragIndex : Number.parseInt(raw, 10);
    setDragIndex(null);
    if (sourceIndex === null || Number.isNaN(sourceIndex)) return;
    move(sourceIndex, targetIndex, channels[sourceIndex]?.id);
  }

  function onDragEnd() {
    setDragIndex(null);
  }

  function onHandleKeyDown(
    ev: KeyboardEvent<HTMLSpanElement>,
    index: number,
    channelId: string,
  ) {
    if (ev.key === "ArrowUp") {
      ev.preventDefault();
      move(index, index - 1, channelId);
    } else if (ev.key === "ArrowDown") {
      ev.preventDefault();
      move(index, index + 1, channelId);
    }
  }

  return (
    <ul
      role="list"
      aria-label={t("notifications.channelOrder.listAriaLabel")}
      data-testid="channel-order-list"
      className="flex flex-col gap-sm"
    >
      {channels.map((channel, index) => {
        const isFirst = index === 0;
        const isLast = index === channels.length - 1;
        const handleRefocus = (el: HTMLSpanElement | null) => {
          if (el && refocusIdRef.current === channel.id) {
            el.focus();
            refocusIdRef.current = null;
          }
        };
        return (
          <li
            key={channel.id}
            data-testid={`channel-order-row-${channel.id}`}
            draggable
            onDragStart={(ev) => onDragStart(ev, index)}
            onDragOver={onDragOver}
            onDrop={(ev) => onDrop(ev, index)}
            onDragEnd={onDragEnd}
            className={`flex items-center gap-sm rounded-md border border-brand-primary/10 bg-white p-sm ${
              dragIndex === index ? "opacity-60" : ""
            }`}
          >
            <span
              ref={handleRefocus}
              role="button"
              tabIndex={0}
              aria-label={t("notifications.channelOrder.dragHandleLabel", {
                channel: channel.label,
              })}
              data-testid={`channel-order-handle-${channel.id}`}
              onKeyDown={(ev) => onHandleKeyDown(ev, index, channel.id)}
              className="inline-flex h-6 w-6 cursor-grab select-none items-center justify-center text-neutral-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              <span aria-hidden="true">⋮⋮</span>
            </span>
            <span
              data-testid={`channel-order-position-${channel.id}`}
              aria-hidden="true"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary/10 text-xs font-semibold text-brand-primary"
            >
              {index + 1}
            </span>
            <span className="flex-1 text-sm font-medium text-brand-primary">
              {channel.label}
            </span>
            <button
              type="button"
              onClick={() => move(index, index - 1, channel.id)}
              disabled={isFirst}
              aria-label={t("notifications.channelOrder.moveUp", {
                channel: channel.label,
              })}
              data-testid={`channel-order-up-${channel.id}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brand-primary/20 bg-white text-brand-primary hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span aria-hidden="true">↑</span>
            </button>
            <button
              type="button"
              onClick={() => move(index, index + 1, channel.id)}
              disabled={isLast}
              aria-label={t("notifications.channelOrder.moveDown", {
                channel: channel.label,
              })}
              data-testid={`channel-order-down-${channel.id}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-brand-primary/20 bg-white text-brand-primary hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span aria-hidden="true">↓</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
